import React, { useState, useEffect, useCallback } from 'react';
import dayjs from 'dayjs';
import Link from 'next/link';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';

import {
    Box,
    Type,
    P,
} from '@saladbob/sassafras'

import styles from './bracket.module.css';

import { PiTrophy, PiToilet } from 'react-icons/pi';

import { fetchAdmin } from '../../hooks/useSwr';

import { socket } from '../service/socket';

import { Game as GameType, Team } from '../../types';

type TeamProps = {
    team: Team,
    loser?: Team,
    place: string,
    position: string,
    mb?: string,
    mt?: string,
};

type StatusProps = {
    status: string,
    color?: string,
};

type GameProps = {
    game: GameType,
    topLabel?: boolean,
    bottomLabel?: boolean,
    tb?: boolean,
    trophy?: 'trophy' | 'toilet',
    trophyColor?: string | [string, string],
    notes?: string,
};

dayjs.extend(utc);
dayjs.extend(timezone);

dayjs.tz.setDefault('America/New_York');

const trophyIcon = {
    trophy: () => <PiTrophy size={32} />,
    toilet: () => <PiToilet size={32} />,
};

function truncateString(str, num) {
    if (!str) return str;
    if (str.length <= num) {
        return str
    }
    return str.slice(0, num) + '...'
}

const TeamCard: React.FC<TeamProps> = ({ team, loser, place, position, mb, mt }) => {
    return (
    <P
        className={`${styles.gameTeamName} ${position === 'top' ? styles.gameTeamTop : styles.gameTeamBottom}`}
        font="control"
        weight="bold"
        ps="md"
        mb={mb || 0}
        mt={mt}
        width="auto"
        decoration={loser && loser.id === team.id ? 'line-through' : null}
        color={loser && loser.id === team.id ? 'neutral' : null}
        style={{
            position: 'absolute',
            bottom: position === 'top' ? '100%' : null,
            top: position === 'bottom' ? '100%' : null,
            width: 'auto',
            zIndex: 1,
        }}>
        {team ? truncateString(team.name, 20) : place}
    </P>
)};

const Status: React.FC<StatusProps> = ({status, color}) => {
    return (
    <Box rounded="xxs" display="inline-flex" width="auto" padding={[0, 'xs']} txtColor="white" bgColor={color || 'neutral'} align="center" me="auto">
        <Type font="p" align="center" weight="bold" margin="0">{status}</Type>
    </Box>
)};

const Game: React.FC<GameProps> = ({ game, topLabel, bottomLabel, tb, trophy, trophyColor = 'tertiary600', notes }) => {
    const { admin } = fetchAdmin();
    const [score1, setScore1] = useState(game?.team1Score || 0);
    const [score2, setScore2] = useState(game?.team2Score || 0);
    const [status, setStatus] = useState(game?.status || 'pending');
    const [team1, setTeam1] = useState(game?.team1);
    const [team2, setTeam2] = useState(game?.team2);
    const [winner, setWinner] = useState(game?.winner);
    const [loser, setLoser] = useState(game?.loser);

    const updateScores = useCallback((data) =>{
        if (data.id === game.id) {
            setScore1(data.team1Score);
            setScore2(data.team2Score);
        }
    }, [game.id]);;

    const updateStatus = useCallback((data) =>{
        if (data.id === game.id) {
            setStatus(data.status);
        }
    }, [game.id]);

    const updateTeam1 = useCallback((data) =>{
        if (data.id === game.id) {
            setTeam1(data.team1);
        }
    }, [game.id]);

    const updateTeam2 = useCallback((data) =>{
        if (data.id === game.id) {
            setTeam2(data.team2);
        }
    }, [game.id]);

    const updateWinner = useCallback((data) =>{
        if (data.id === game.id) {
            setWinner(data.winner);
        }
    }, [game.id]);

    const updateLoser = useCallback((data) =>{
        if (data.id === game.id) {
            setLoser(data.loser);
        }
    }, [game.id]);

    useEffect(() => {
        if (!game) {
            return;
        }

        socket.on('scoreChange', updateScores);
        socket.on('gameStatusChange', updateStatus);
        socket.on('gameTeam1Change', updateTeam1);
        socket.on('gameTeam2Change', updateTeam2);
        socket.on('gameWinnerChange', updateWinner);
        socket.on('gameLoserChange', updateLoser);
        return () => {
            socket.off('scoreChange', updateScores);
            socket.off('gameStatusChange', updateStatus);
            socket.off('gameTeam1Change', updateTeam1);
            socket.off('gameTeam2Change', updateTeam2);
            socket.off('gameWinnerChange', updateWinner);
            socket.off('gameLoserChange', updateLoser);
        }
    }, [game, updateLoser, updateScores, updateStatus, updateTeam1, updateTeam2, updateWinner]);

    if (!game) {
        return null;
    }
    return (
        <Box className={`${styles.game}${game.type === 'contingent' ? ` ${styles.gameContingent}` : ''}`} minWidth="260px" style={{ height: '100%', position: 'relative' }}>
            <Box
                className={styles.gameBorder}
                bdWidth="2px"
                bdColor={winner && game.team1 && winner.id === game.team1?.id ? 'secondary' : 'black'}
                style={{
                    position: 'absolute',
                    height: '50%',
                    right: 0,
                    width: topLabel ? 'calc(100% - 24px)' : '100%',
                    borderBottom: 'none',
                    borderLeft: 'none',
                }}
            />

            <Box
                className={styles.gameBorder}
                bdWidth="2px"
                bdColor={winner && game.team2 && winner.id === game.team2?.id ? 'secondary' : 'black'}
                style={{
                    position: 'absolute',
                    height: '50%',
                    right: 0,
                    width: bottomLabel ? 'calc(100% - 24px)' : '100%',
                    bottom: 0,
                    borderTop: 'none',
                    borderLeft: 'none',
                }}
            />
            <Box height="100%" verticalAlign="center" alignItems="flex-end" >
                {(topLabel || team1) && (<TeamCard team={team1} place={game.place1} loser={loser} mb="" position="top"/>)}

                {admin && game.status && game.status !== 'pending' && (
                    <Box
                        className={styles.gameScore}
                        width="auto"
                        padding={[0, 'xs']}
                        bgColor={score1 > score2 ? 'secondary' : 'black'}
                        txtColor="white"
                        style={{ position: 'absolute', top: '1px', right: '1px' }}
                    >
                        <Type font="control" weight={score1 > score2 ? 'bold' : null}>{score1}</Type>
                    </Box>
                )}

                <Box padding={['sm', 'xl', 'sm', 0]} justify="center" align="center" className={styles.gameDetails}>
                    <div className={styles.gameMeta}>
                        <Type tag="p" weight="bold" align="right" font="control" color="primary" className={styles.gameTitle}>
                            <Link href={`/game/${game.id}`} target="_blank">
                                {game.title}
                            </Link>
                        </Type>
                        {status === 'live' && (<Status status="LIVE" color="primary"/>)}
                        {status === 'forfeit' && (<Status status="FORFEIT"/>)}
                        {status === 'ended' && (<Status status="FINAL"/>)}
                        {status === 'bye' && (<Status status="BYE" />)}
                        {(status === 'pending' || status === 'waiting') && (
                            <Box align="center" justify="center">
                                <Type tag="p" weight="bold" align="right" font="small" className={styles.gameTime}>
                                    {game.type === 'contingent' && (
                                        <>
                                            IF NECCESARY
                                            <br/>
                                        </>
                                    )}
                                    {dayjs(game.time).tz('America/New_York').format('ddd h:mm A')}
                                </Type>
                                {/* {admin && (
                                    <Btn size="xs" ms="xs" outline color="neutral">
                                        <Edit2 size={16} />
                                    </Btn>
                                )} */}
                            </Box>
                        )}
                        
                    </div>
                </Box>

                {admin && game.status && game.status !== 'pending' && (
                    <Box
                        className={styles.gameScore}
                        width="auto"
                        padding={[0, 'xs']}
                        bgColor={score2 > score1 ? 'secondary' : 'black'}
                        txtColor="white"
                        style={{ position: 'absolute', bottom: '1px', right: '1px' }}
                    >
                        <Type font="control" weight={score2 > score1 ? 'bold' : null}>{score2}</Type>
                    </Box>
                )}

                {(bottomLabel || team2) && (<TeamCard team={team2} place={game.place2} loser={loser} position="bottom" />)}
            </Box>
            {(trophy || tb) && (
                <div className={`${styles.trophy} ${styles.trophycenter}`}>
                    <Box
                        width="56px"
                        height="56px"
                        bgColor={typeof trophyColor === 'string' ? trophyColor : trophyColor[0]}
                        txtColor={typeof trophyColor === 'string' ? 'white' : trophyColor[1]}
                        rounded="100%"
                        align="center"
                        justify="center"
                    >
                        {trophyIcon[trophy]()}
                    </Box>
                </div>
            )}
            {notes && (
                <Box
                    className={styles.gameNotes}
                    bgColor="neutral100"
                    padding="sm"
                    width="auto"
                    style={{ position: 'absolute', top: 0, right: 0 }}
                >
                    <Type font="control" weight="bold" align="center" color="primary">{notes}</Type>
                </Box>
            )}
        </Box>
    );
}
export default Game;
